import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import { SelectAutoOpen } from "../../components/SelectAutoOpen";
import {
  IComponente,
  ISelectOption,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
} from "../types/componente.types";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabGeralProps {
  componente: Partial<IComponente>;
  onChange: (campo: keyof IComponente, valor: any) => void;
  referencias: ISelectOption[];
  linhas: ISelectOption[];
  grupos: ISelectOption[];
  cores: ISelectOption[];
  unidades: ISelectOption[];
  onOpenMidias: () => void;
  // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 12/C11): alerta de código já existente
  // exibido no próprio campo assim que o usuário sai dele (como no cadastro de Perfil).
  codigoDuplicado?: boolean;
}

export const TabGeral: React.FC<TabGeralProps> = ({
  componente,
  onChange,
  referencias,
  linhas,
  grupos,
  cores,
  unidades,
  onOpenMidias,
  codigoDuplicado = false,
}) => {
  // Determinar se o campo Cor deve estar habilitado
  const corHabilitada = componente.aplicacao === 'cor_unica';
  const corObrigatoria = componente.aplicacao === 'cor_unica';

  return (
    <Box>
      {/* FASE 05 — 6ª validação/PO 2026-08-11: Situação numa linha ACIMA do container principal,
          para que Código | Código interno | Status fiquem alinhados ao topo do quadro da imagem. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel sx={labelSx}>Situação</InputLabel>
            <SelectAutoOpen value={componente.idSituacao || 1} label="Situação" disabled sx={selectSx}>
              {SITUACAO_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
              ))}
            </SelectAutoOpen>
          </FormControl>
        </Grid>
      </Grid>
      {/* Container principal com Imagem à esquerda e campos à direita */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagem do Produto */}
        <Grid item xs={12} md={3}>
          {/* Container da Imagem - COM BORDA CINZA */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#333", mb: 2, fontWeight: 500 }}
            >
              Imagem do produto
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                backgroundColor: "#fff",
              }}
            >
              {componente.imagemUrl ? (
                <img
                  src={componente.imagemUrl}
                  alt="Imagem do produto"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem
                </Typography>
              )}
            </Box>

            {componente.imagemNomeArquivo && (
              <Typography
                variant="caption"
                sx={{ color: "#999", textAlign: "center", display: "block", mt: 2 }}
              >
                {componente.imagemNomeArquivo}
              </Typography>
            )}
          </Box>

          {/* Botão FORA do container da imagem */}
          <Button
            variant="contained"
            fullWidth
            onClick={onOpenMidias}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
              textTransform: "none",
              py: 1.2,
            }}
          >
            GERENCIAMENTO DE MÍDIAS
          </Button>
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: mockup do PO (image5) —
              linha Código | Código interno e Descrição em linha própria (campo largo). */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={componente.codigo || ""}
                onChange={(e) => onChange("codigo", e.target.value.toUpperCase())}
                required
                inputProps={{ maxLength: 50 }}
                error={codigoDuplicado}
                // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem padronizada do blur
                // (a mensagem longa fica só no Salvar).
                helperText={codigoDuplicado ? "Código já existe" : undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={componente.codigoInterno || ""}
                onChange={(e) => onChange("codigoInterno", e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={inputSx}
              />
            </Grid>
            {/* FASE 05 — 6ª validação/PO 2026-08-07: Status na 3ª coluna, alinhado com Código/Código
                interno (Situação fica na linha acima). */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Status</InputLabel>
                <SelectAutoOpen value={componente.idStatus || 1} label="Status" onChange={(e) => onChange("idStatus", e.target.value)} sx={selectSx}>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Descrição */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={componente.descricao || ""}
                onChange={(e) => onChange("descricao", e.target.value)}
                required
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Referência, Linha, Grupo, Aplicação */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Referência</InputLabel>
                <SelectAutoOpen
                  value={componente.idReferencia || ""}
                  label="Referência"
                  onChange={(e) => {
                    const valor = e.target.value;
                    onChange("idReferencia", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao}
                    </MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Linha</InputLabel>
                <SelectAutoOpen
                  value={componente.idLinha || ""}
                  label="Linha"
                  onChange={(e) => {
                    const valor = e.target.value;
                    onChange("idLinha", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha.id} value={linha.id}>
                      {linha.nome || linha.descricao}
                    </MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Grupo</InputLabel>
                <SelectAutoOpen
                  value={componente.idGrupo || ""}
                  label="Grupo"
                  onChange={(e) => {
                    const valor = e.target.value;
                    onChange("idGrupo", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {grupos.map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      {grupo.nome || grupo.descricao}
                    </MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
            {/* FASE 05 — 2ª validação/PO 2026-07-24 (D9): campo "Aplicação" (Cor única/Colorido)
                removido; o cadastro é sempre tratado como Cor única. "Colorido" é backlog futuro. */}
          </Grid>

          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: mockup do PO (image5) —
              linha Cor | Unidade item | Unidade embalagem (Quantidade embalagem desce). */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {corHabilitada && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={labelSx} required={corObrigatoria}>
                    Cor
                  </InputLabel>
                  <SelectAutoOpen
                    value={componente.idCor || ""}
                    label="Cor"
                    onChange={(e) => {
                      const valor = e.target.value;
                      onChange("idCor", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                    }}
                    required={corObrigatoria}
                    disabled={!corHabilitada}
                    sx={selectSx}
                  >
                    <MenuItem value="">
                      <em>Selecione...</em>
                    </MenuItem>
                    {cores.map((cor) => (
                      <MenuItem key={cor.id} value={cor.id}>
                        {cor.nome || cor.descricao}
                      </MenuItem>
                    ))}
                  </SelectAutoOpen>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Unidade item</InputLabel>
                <SelectAutoOpen
                  value={componente.idUnidadeItem || ""}
                  label="Unidade item"
                  onChange={(e) => {
                    const valor = e.target.value;
                    onChange("idUnidadeItem", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  required
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {unidades.map((unidade) => (
                    <MenuItem key={unidade.id} value={unidade.id}>
                      {unidade.nome || unidade.descricao}
                    </MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Unidade embalagem</InputLabel>
                <SelectAutoOpen
                  value={componente.idUnidadeEmbalagem || ""}
                  label="Unidade embalagem"
                  onChange={(e) => {
                    const valor = e.target.value;
                    onChange("idUnidadeEmbalagem", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  required
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {unidades.map((unidade) => (
                    <MenuItem key={unidade.id} value={unidade.id}>
                      {unidade.nome || unidade.descricao}
                    </MenuItem>
                  ))}
                </SelectAutoOpen>
              </FormControl>
            </Grid>
          </Grid>

          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: mockup do PO (image5) —
              linha Quantidade embalagem | Lote mínimo | Otimização de barra. */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Quantidade embalagem"
                type="number"
                // FASE 05 — 2ª validação/PO 2026-07-24 (D5): permite apagar durante a
                // digitação (inclusive o "1"); coage para o mínimo 1 ao sair do campo.
                value={componente.quantidadeEmbalagem ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  onChange("quantidadeEmbalagem", raw === "" ? undefined : parseInt(raw));
                }}
                onBlur={(e) => {
                  const valor = parseInt(e.target.value);
                  onChange("quantidadeEmbalagem", isNaN(valor) || valor < 1 ? 1 : valor);
                }}
                required
                inputProps={{ min: 1 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Quantidade mínima de embalagens por pedido.">
                <TextField
                  fullWidth
                  label="Lote mínimo (Qtd. de embalagens)"
                  type="number"
                  // FASE 05 — 2ª validação/PO 2026-07-24 (D5): idem Quantidade embalagem.
                  value={componente.loteMinimoQtdEmbalagens ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    onChange("loteMinimoQtdEmbalagens", raw === "" ? undefined : parseInt(raw));
                  }}
                  onBlur={(e) => {
                    const valor = parseInt(e.target.value);
                    onChange("loteMinimoQtdEmbalagens", isNaN(valor) || valor < 1 ? 1 : Math.min(99999, valor));
                  }}
                  required
                  inputProps={{ min: 1, max: 99999, maxLength: 5 }}
                  sx={inputSx}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Otimização de barra"
                value={componente.otimizacaoBarra || ""}
                onChange={(e) => onChange("otimizacaoBarra", e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
